import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { GradientBorderContainer } from "@/components/GradientBorderContainer";
import SendolaBusinessLogo from "@/assets/Img/sendolapay-business-logo-white.png";
import SendolaBusinessBlackLogo from "@/assets/Img/sendolapay-business-logo.png";
import MexicoFlag from "@/assets/Img/Flags/flag-mexico.png";
import CanadaFlag from "@/assets/Img/Flags/flag-canada.png";
import CanadianMan from "@/assets/Img/canadian-man.png";
import AmericaMap from "@/assets/Icons/america.svg?react";
import ArrowGreen from "@/assets/Icons/arrow-green.svg?react";
import GreenCheck from "@/assets/Icons/check-green.svg?react";
import EuropeMap from "@/assets/Icons/europe.svg?react";
import HubspotContactForm from "@/components/HubspotContactForm";
import { BusinessCheckItem } from "@/components/BusinessCheckItem";
import { useNavigate } from "react-router";
import { Modal } from "@/components/Modal";
import SendolaPayVideoEs from "@/assets/Video/sendolapay-video-es.mp4";
import SendolaPayVideoEn from "@/assets/Video/sendolapay-video.mp4";
import { useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import {
  CaboSection,
  CountryDropdown,
  SendolaBusinessCard,
  SendolaPayCard,
} from "@/components/Cabo";
import sendolaPayLogo from "@/assets/Img/logo-sendola-pay.png";
import classNames from "classnames";

const AuthUrl = import.meta.env.VITE_AUTH_DOMAIN;

export default function CaboPage() {
  const navigate = useNavigate();
  const {
    t,
    i18n: { language: lang },
  } = useTranslation("cabo");
  const videoRefEn = useRef<HTMLVideoElement>(null);
  const videoRefEs = useRef<HTMLVideoElement>(null);
  const audioEnabled = useRef(false);
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    useState<boolean>(false);
  const [loadingForm, setLoadingForm] = useState<boolean>(true);

  const [countrySelected, setCountrySelected] = useState<"USA" | "CAN">("USA");

  useEffect(() => {
    const videoRef = lang === "en" ? videoRefEn : videoRefEs;
    const otherVideoRef = lang !== "en" ? videoRefEn : videoRefEs;
    if (otherVideoRef?.current && videoRef?.current) {
      videoRef.current.muted = otherVideoRef.current.muted;
      videoRef.current.volume = otherVideoRef.current.volume;
      videoRef.current.currentTime = otherVideoRef.current.currentTime;

      otherVideoRef.current.pause();
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (videoRef?.current && !isMobile) {
            videoRef.current.play().catch(() => {});

            if (!audioEnabled.current) {
              videoRef.current.muted = false;
              audioEnabled.current = true;
            }
          }
        } else {
          if (videoRef?.current && !isMobile) {
            videoRef.current.pause();
          }
        }
      },
      { threshold: 0.1 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      observer.disconnect();
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, [lang]);

  return (
    <>
      <Helmet>
        <title>SendolaPay Cabo | Inicio</title>
      </Helmet>

      <div className="w-full flex flex-col bg-white">
        <div className="w-full flex justify-center pt-10 pb-8 bg-gradient-to-br from-primary-100 to-white rounded-b-large shadow-md shadow-gray-200">
          <div className="flex flex-col gap-10 justify-center items-center w-full max-w-screen-xl">
            <h1
              className="text-center text-2xl font-extralight [&_b]:font-semibold"
              dangerouslySetInnerHTML={{ __html: t("title") }}
            ></h1>
            <p
              className="text-center text-lg font-extralight [&_b]:font-medium px-4 max-w-2xl"
              dangerouslySetInnerHTML={{ __html: t("description") }}
            ></p>
            <div className="w-full flex justify-center mb-2">
              <CountryDropdown
                value={countrySelected}
                onChange={setCountrySelected}
              />
            </div>
            <div className="w-full flex flex-col gap-4 justify-center items-center px-8 pb-6">
              <h2 className="text-xl md:text-2xl font-medium">
                {t("chooseSolution")}
              </h2>
              <div className="flex flex-col lg:flex-row gap-8 mt-4">
                <SendolaPayCard
                  country={countrySelected}
                  className="w-full lg:w-6/12"
                  onClickLearnMore={() => {
                    window.location.href = "#individuals";
                  }}
                />
                <SendolaBusinessCard
                  country={countrySelected}
                  className="w-full lg:w-6/12"
                  onClickLearnMore={() => {
                    window.location.href = "#businesses";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          id="individuals"
          className="flex flex-col items-center gap-4 px-8 py-8"
        >
          <div className="flex flex-col lg:flex-row items-center gap-4">
            <img
              src={sendolaPayLogo}
              alt="SendolaPay Logo"
              className="w-52 h-auto"
            />
            <p className="text-lg font-medium">
              {t("solutions.sendola.forIndividuals")}
            </p>
          </div>
          <div className="max-w-screen-xl">
            <CaboSection
              country={countrySelected}
              onClickSignUp={() =>
                window.open(`${AuthUrl}/sign-up?source=loscabos`, "_blank")
              }
            />
          </div>
          {countrySelected === "CAN" && (
            <div className="max-w-screen-md py-8">
              <p className="text-center text-sm font-light text-gray-600">
                {t("solutions.sendola.canadaNotAvailable")}
              </p>
            </div>
          )}
          <div className="w-full flex flex-col justify-center items-center max-w-screen-xl pb-8 gap-6 lg:gap-14">
            <p
              className="text-slate-800 text-2xl sm:text-3xl text-center md:mt-6 font-light [&_b]:font-medium"
              dangerouslySetInnerHTML={{
                __html: t("solutions.sendola.changingTheWay"),
              }}
            />
            {countrySelected === "USA" && (
              <>
                <div
                  className={classNames(
                    "rounded-2xl overflow-hidden h-auto w-full",
                    {
                      hidden: lang !== "en",
                    }
                  )}
                >
                  <video
                    ref={videoRefEn}
                    muted
                    autoPlay
                    loop
                    playsInline
                    controls
                    height={1080}
                    width={1920}
                    disablePictureInPicture
                    disableRemotePlayback
                    className="w-full h-fit min-h-full max-w-full"
                  >
                    <source src={SendolaPayVideoEn} type="video/mp4" />
                  </video>
                </div>
                <div
                  className={classNames(
                    "rounded-2xl overflow-hidden h-auto w-full",
                    {
                      hidden: lang !== "es",
                    }
                  )}
                >
                  <video
                    ref={videoRefEs}
                    muted
                    autoPlay
                    loop
                    playsInline
                    controls
                    height={1080}
                    width={1920}
                    disablePictureInPicture
                    disableRemotePlayback
                    className="w-full h-fit min-h-full max-w-full"
                  >
                    <source src={SendolaPayVideoEs} type="video/mp4" />
                  </video>
                </div>
              </>
            )}
            <div className="w-full flex justify-center">
              <button
                type="button"
                onClick={() => {
                  window.open("/", "_blank");
                }}
                className="bg-[#0A5BFF] hover:bg-[#0047b3] text-white font-bold py-3 px-8 rounded-full text-lg shadow transition"
              >
                {t("solutions.sendola.visit")}
              </button>
            </div>
          </div>
        </div>

        <div
          id="businesses"
          style={{
            background:
              "radial-gradient(circle at 90% 95%,#073823 0%, #0c0c0c 24%)",
          }}
          className="w-full flex flex-col items-center bg-[#0c0c0c] gap-8 text-white py-10"
        >
          <div className="w-full flex flex-col md:flex-row justify-center items-center gap-x-10">
            <img src={SendolaBusinessLogo} className="h-20 w-auto" />

            <p className="font-medium lg:text-lg">
              {t("solutions.business.forBusiness")}
            </p>
          </div>
          <div className="w-full flex items-center justify-around max-w-screen-xl px-6 md:px-10">
            <div className="flex flex-col gap-6 lg:gap-10 max-w-xl">
              <p
                className="w-full text-3xl xs:text-4xl sm:text-5xl md:text-4xl lg:text-5xl font-light [&_span]:font-semibold [&_span]:text-business-green4 md:pl-4"
                dangerouslySetInnerHTML={{
                  __html: t("solutions.business.headingTitle"),
                }}
              />

              <div className="flex items-center gap-2">
                <GradientBorderContainer
                  className="rounded-full"
                  classNameContainer="rounded-full !py-3 !px-4"
                  onClick={() => setIsCreateAccountModalOpen(true)}
                >
                  {t("solutions.business.createAccount")}
                </GradientBorderContainer>
                <Button
                  className="!bg-white !border-none !font-light !text-black !py-3 !px-4 min-w-fit"
                  text={t("solutions.business.contact")}
                  onClick={() => navigate("/business/contact")}
                />
              </div>

              <div className="flex flex-col md:hidden justify-center items-center gap-8 border-[2px] border-sendolab-gray bg-sendolab-gray3/50 backdrop-blur-md rounded-3xl px-4 py-6">
                <p className="text-lg text-sendolab-white font-light text-center max-w-sm">
                  {t("solutions.business.sendMoney.readyToLevel")}
                </p>
                <GradientBorderContainer
                  className="w-full"
                  classNameContainer="bg-sendolab-gray3 flex gap-4 justify-between py-2 select-none"
                >
                  <span className="w-12 text-center text-[80px] text-business-green3 font-bold -mt-8 leading-none">
                    1
                  </span>
                  <span className="flex items-center flex-1 text-center px-3">
                    {t("solutions.business.steps.1")}
                  </span>
                </GradientBorderContainer>
                <GradientBorderContainer
                  className="w-full"
                  classNameContainer="bg-sendolab-gray3 flex gap-4 justify-between py-2 select-none"
                >
                  <span className="w-12 text-center text-[80px] text-business-green3 font-bold -mt-8 leading-none">
                    2
                  </span>
                  <span className="flex items-center flex-1 text-center px-3">
                    {t("solutions.business.steps.2")}
                  </span>
                </GradientBorderContainer>
                <GradientBorderContainer
                  className="w-full"
                  classNameContainer="bg-sendolab-gray3 flex gap-4 justify-between py-2 select-none"
                >
                  <span className="w-12 text-center text-[80px] text-business-green3 font-bold -mt-8 leading-none">
                    3
                  </span>
                  <span className="flex items-center flex-1 text-center px-3">
                    {t("solutions.business.steps.3")}
                  </span>
                </GradientBorderContainer>
              </div>
            </div>

            {countrySelected === "USA" ? (
              <div className="hidden md:block rounded-3xl border-[2px] border-sendolab-gray2 bg-gradient-to-br from-black to-sendolab-gray6 py-8 px-10 max-w-sm">
                <div className="flex flex-col gap-4">
                  <div className="w-full flex flex-col mt-6 pr-4">
                    <p className="text-sendolab-gray5 font-extralight uppercase tracking-[4px] pl-3">
                      {t("solutions.business.example.balance")}:
                    </p>
                    <div className="w-full relative flex items-center gap-2 border-[1px] border-sendolab-gray/80 rounded-2xl px-3 py-2">
                      <p className="text-sendolab-gray5 font-extralight uppercase">
                        MXN:
                      </p>

                      <p className="text-white font-extralight text-lg">
                        $1,646,809.68
                      </p>

                      <div className="absolute right-0 rounded-full border-[1px] border-business-green3 w-12 h-12 translate-x-4 p-[6px] bg-gradient-to-r from-black to-sendolab-gray3">
                        <img
                          className="w-full h-full rounded-full object-cover"
                          src={MexicoFlag}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="w-full flex flex-col mt-4">
                    <p className="text-sendolab-gray5 font-extralight uppercase tracking-[4px] pl-3">
                      *{t("solutions.business.example.amountToSend")}:
                    </p>
                    <div className="flex items-center gap-2 border-[1px] border-sendolab-gray/80 rounded-2xl px-3 py-2">
                      <p className="text-sendolab-gray5 font-extralight uppercase">
                        USD:
                      </p>

                      <p className="text-white font-extralight text-lg">
                        $82,252.47
                      </p>
                    </div>

                    <p className="text-sendolab-gray5 font-extralight uppercase tracking-[4px] pl-3 mt-2">
                      *{t("solutions.business.example.savings")}:
                    </p>
                    <GradientBorderContainer classNameContainer="py-2 px-3">
                      <div className="flex items-center w-full gap-2">
                        <p className="text-sendolab-gray5 font-extralight uppercase">
                          USD:
                        </p>

                        <p className="text-white font-extralight text-lg">
                          $12,300.00
                        </p>
                      </div>
                    </GradientBorderContainer>
                  </div>

                  <p className="text-[10px] text-sendolab-gray5 text-center mt-4">
                    {t("solutions.business.example.disclaimer")}
                  </p>
                </div>
              </div>
            ) : (
              <img
                className="hidden md:block w-auto h-[22rem] lg:h-[28rem] xl:h-[36rem] -mt-[4rem] lg:-mt-[6rem] lg:-mr-8"
                src={CanadianMan}
              />
            )}
          </div>
          <div className="w-full sm:px-10 flex justify-center md:mt-8">
            <GradientBorderContainer
              className="w-full max-w-screen-lg max-md:!bg-transparent max-md:!border-none !rounded-3xl"
              classNameContainer="flex items-center justify-center py-6 max-md:!bg-transparent !rounded-3xl"
              gradientColor="transparent"
            >
              <div className="flex flex-col gap-8 w-full">
                {countrySelected === "USA" ? (
                  <div className="flex flex-col items-center justify-center gap-4">
                    <p className="text-xl md:text-2xl text-white font-extralight">
                      {t("solutions.business.sendMoney.title")}
                    </p>
                    <div className="border-[2px] border-sendolab-gray bg-sendolab-gray3/50 backdrop-blur-md rounded-3xl px-10 py-4 w-full md:w-fit flex items-center justify-center">
                      <div className="flex items-center gap-4 md:gap-10">
                        <div className="flex flex-col-reverse md:flex-row items-center gap-4 text-white">
                          <p className="text-sm font-medium">
                            {t("solutions.sendola.americas")}:
                          </p>
                          <AmericaMap className="w-32 h-32 flex items-center justify-center" />
                        </div>
                        <div className="flex flex-col-reverse md:flex-row items-center gap-4 text-white">
                          <p className="text-sm font-medium">
                            {t("solutions.sendola.europe")}:
                          </p>
                          <EuropeMap className="w-32 h-32 flex items-center justify-center" />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs lg:text-sm text-sendolab-gray5 font-light">
                      {t("solutions.business.conciergeAdvice")}
                    </p>
                  </div>
                ) : (
                  <div className="w-full flex items-center justify-center gap-4 mt-4">
                    <img
                      className="w-16 h-16 rounded-full object-cover"
                      src={CanadaFlag}
                    />
                    <ArrowGreen className="w-11 h-11" />
                    <img
                      className="w-16 h-16 rounded-full object-cover"
                      src={MexicoFlag}
                    />
                  </div>
                )}

                <div className="hidden md:flex flex-col gap-8 items-center justify-center">
                  <div className="flex flex-col gap-1">
                    <p className="text-xl md:text-2xl text-white font-extralight">
                      {t("solutions.business.sendMoney.readyToLevel")}
                    </p>
                    <p className="text-sendolab-gray5 text-center text-lg">
                      {t("solutions.business.sendMoney.makePayments")}
                    </p>
                  </div>
                  <div className="flex justify-center gap-3">
                    <GradientBorderContainer classNameContainer="bg-sendolab-gray3 flex gap-6 justify-between py-2 pr-2 select-none">
                      <span className="text-[80px] text-business-green3 font-bold -mt-8 leading-none">
                        1
                      </span>
                      <span className="flex items-center flex-1 text-sm lg:text-base text-center pr-3">
                        {t("solutions.business.steps.1")}
                      </span>
                    </GradientBorderContainer>
                    <GradientBorderContainer classNameContainer="bg-sendolab-gray3 flex gap-6 justify-between py-2 pr-2 select-none">
                      <span className="text-[80px] text-business-green3 font-bold -mt-8 leading-none">
                        2
                      </span>
                      <span className="flex items-center flex-1 text-sm lg:text-base text-center pr-3">
                        {t("solutions.business.steps.2")}
                      </span>
                    </GradientBorderContainer>
                    <GradientBorderContainer classNameContainer="bg-sendolab-gray3 flex gap-6 justify-between py-2 pr-2 select-none">
                      <span className="text-[80px] text-business-green3 font-bold -mt-8 leading-none">
                        3
                      </span>
                      <span className="flex items-center flex-1 text-sm lg:text-base text-center pr-3">
                        {t("solutions.business.steps.3")}
                      </span>
                    </GradientBorderContainer>
                  </div>
                  <div className="flex justify-center gap-3 pb-3">
                    <GradientBorderContainer
                      className="rounded-full"
                      classNameContainer="rounded-full !py-3 !px-4"
                      onClick={() => setIsCreateAccountModalOpen(true)}
                    >
                      {t("solutions.business.createAccount")}
                    </GradientBorderContainer>
                    <Button
                      className="!bg-white !border-none !font-light !text-black !py-3 !px-4 min-w-fit"
                      text={t("solutions.business.contact")}
                      onClick={() => navigate("/business/contact")}
                    />
                  </div>
                </div>
              </div>
            </GradientBorderContainer>
          </div>
          <div className="w-full flex justify-center max-w-screen-xl md:mt-6 px-6 md:px-10">
            <div className="w-full flex flex-col gap-10">
              <p className="text-2xl md:text-3xl text-white font-medium text-center">
                {t("solutions.business.service")}
              </p>
              <div className="w-full flex flex-col lg:flex-row gap-10 justify-around items-center">
                <div className="h-full w-full flex flex-col gap-3 items-center lg:max-w-[46%]">
                  <p
                    className="w-full text-lg md:text-xl font-light text-center [&_span]:font-semibold text-sendolab-white mb-8"
                    dangerouslySetInnerHTML={{
                      __html: t("solutions.business.smarter"),
                    }}
                  />

                  <BusinessCheckItem
                    title={t("solutions.business.advantages.1.title")}
                    content={t("solutions.business.advantages.1.content")}
                  />
                  <BusinessCheckItem
                    title={t("solutions.business.advantages.2.title")}
                    content={t("solutions.business.advantages.2.content")}
                  />
                  <BusinessCheckItem
                    title={t("solutions.business.advantages.3.title")}
                    content={t("solutions.business.advantages.3.content")}
                  />
                  <BusinessCheckItem
                    title={t("solutions.business.advantages.4.title")}
                    content={t("solutions.business.advantages.4.content")}
                  />
                  <p className="w-full text-xs text-sendolab-gray5 mt-2 px-3">
                    {t("solutions.business.advantages.disclaimer")}
                  </p>
                </div>

                <div className="h-full flex flex-col gap-4 items-center lg:max-w-[46%]">
                  <p
                    className="w-full text-lg md:text-xl font-light text-center [&_span]:font-semibold text-sendolab-white"
                    dangerouslySetInnerHTML={{
                      __html: t("solutions.business.flexible"),
                    }}
                  />

                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <GradientBorderContainer
                      className="w-full rounded-[24px] !min-h-48"
                      classNameContainer="rounded-[24px] flex flex-col gap-2"
                    >
                      <GreenCheck className="w-5 h-5" />
                      <p className="text-white">
                        {t("solutions.business.settlementOptions.1.title")}
                      </p>
                      <p className="text-xs font-light text-sendolab-white">
                        {t("solutions.business.settlementOptions.1.content")}
                      </p>
                    </GradientBorderContainer>
                    <GradientBorderContainer
                      className="w-full rounded-[24px] !min-h-48"
                      classNameContainer="rounded-[24px] flex flex-col gap-2"
                    >
                      <GreenCheck className="w-5 h-5" />
                      <p className="text-white">
                        {t("solutions.business.settlementOptions.2.title")}**
                      </p>
                      <p className="text-xs font-light text-sendolab-white">
                        {t("solutions.business.settlementOptions.2.content")}
                      </p>
                    </GradientBorderContainer>
                    <GradientBorderContainer
                      className="w-full rounded-[24px] !min-h-48"
                      classNameContainer="rounded-[24px] flex flex-col gap-2"
                    >
                      <GreenCheck className="w-5 h-5" />
                      <p className="text-white">
                        {t("solutions.business.settlementOptions.3.title")}
                      </p>
                      <p className="text-xs font-light text-sendolab-white">
                        {t("solutions.business.settlementOptions.3.content")}
                      </p>
                    </GradientBorderContainer>
                    <GradientBorderContainer
                      className="w-full rounded-[24px] !min-h-48"
                      classNameContainer="rounded-[24px] flex flex-col gap-2"
                    >
                      <GreenCheck className="w-5 h-5" />
                      <p className="text-white">
                        {t("solutions.business.settlementOptions.4.title")}
                      </p>
                      <p className="text-xs font-light text-sendolab-white">
                        {t("solutions.business.settlementOptions.4.content")}
                      </p>
                      <p className="text-[11px] text-sendolab-gray5">
                        {t("solutions.business.settlementOptions.4.disclaimer")}
                      </p>
                    </GradientBorderContainer>
                  </div>
                  <p className="w-full text-xs text-sendolab-gray5 px-3">
                    **{t("solutions.business.settlementOptions.disclaimer")}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full flex justify-center max-w-screen-xl gap-4 py-10 px-6 md:px-10">
            <p className="text-center text-xs sm:text-sm md:text-base lg:text-xl text-sendolab-white font-extralight uppercase md:tracking-[4px]">
              {t("solutions.business.realTime")}
            </p>
            <div className="h-full w-[2px] bg-sendolab-white flex-shrink-0" />
            <p className="text-center text-xs sm:text-sm md:text-base lg:text-xl text-sendolab-white font-extralight uppercase md:tracking-[4px]">
              {t("solutions.business.transparency")}
            </p>
            {lang === "en" && (
              <>
                <div className="h-full w-[2px] bg-sendolab-white flex-shrink-0" />
                <p className="text-center text-xs sm:text-sm md:text-base lg:text-xl text-sendolab-white font-extralight uppercase md:tracking-[4px]">
                  {t("solutions.business.priceMatching")}
                </p>
              </>
            )}
          </div>

          {countrySelected === "CAN" && (
            <p className="w-full text-center text-xs md:text-sm font-medium text-sendolab-gray5 mb-6 px-6 max-w-screen-md">
              {t("solutions.sendola.canadaNotAvailable")}
            </p>
          )}
        </div>
      </div>

      <Modal
        withClose
        open={isCreateAccountModalOpen}
        onClose={() => {
          setIsCreateAccountModalOpen(false);
          setLoadingForm(true);
        }}
      >
        <div className="w-full flex flex-col items-center">
          <div className="w-full pb-8 flex justify-between items-center">
            <img
              className="h-9 sm:h-10 md:h-12 xl:h-14 w-auto object-contain select-none"
              src={SendolaBusinessBlackLogo}
              alt="SendolaPay Business Logo"
            />
          </div>
          {loadingForm && (
            <p className="w-full text-center text-black font-semibold">
              {t("Loading")}...
            </p>
          )}
          <HubspotContactForm
            region="na1"
            portalId="44207752"
            formId="db94a133-c8f4-47bd-9c41-fdefdc87209a"
            onReady={() => {
              setLoadingForm(false);
            }}
          />
        </div>
      </Modal>
    </>
  );
}
